import React, { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { BaseScaleView, BaseStagePlugin, useActiveFrameId, useFramework } from '@/framework';
import type { VectorLayerSVGPreviewPlugin } from './manager';

/**
 * 把 builder 创建的 SVG DOM 挂载到 React Stage 宿主。
 *
 * 该组件不创建或修改 SVG 图形，只负责把外部节点放进正确的 DOM 容器。
 *
 * @param props - SVG 节点与定位样式。
 * @returns SVG DOM 宿主节点。
 */
const SVGPreviewNode = ({ svg, style }: { svg: SVGSVGElement; style: React.CSSProperties }) => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        // svg 默认 display: inline，底边会对齐父级继承 font-size/line-height 生成的文本基线。
        // 当 svg 高度小于该行基线以上的高度时（如高度很小的矢量图层），svg 会被整体下推、与图层位置错开，
        // 改为 block 后 svg 直接贴齐容器左上角，不再参与行内排版。
        svg.style.display = 'block';
        ref.current?.replaceChildren(svg);
    }, [svg]);

    return <div ref={ref} style={style} />;
};

/**
 * 渲染当前全部 Vector SVG previews。
 *
 * @returns Vector SVG Preview Stage；没有活动预览时返回 null。
 */
export const VectorLayerSVGPreview = React.memo(() => {
    const framework = useFramework();
    const manager =
        framework.service.registration.getInstance<VectorLayerSVGPreviewPlugin>('VectorLayerSVGPreviewPlugin');
    const previews = useSyncExternalStore(manager.subscribe, manager.getPreviews, manager.getPreviews);
    const activeFrameId = useActiveFrameId();

    // 多页画布下只渲染当前激活页的预览。
    //
    // 成因：Preview 生命周期与页面无关，commit 后还要等异步的正式 drawable settled 才释放；而 BaseScaleView
    // 仅按图层自身 left/top 定位，不区分所属页面。编辑 Vector 文字时直接切换页面，Text Editor 退出事务需要
    // 先等待 layoutTextAsync 拿到最终字体的排版才会发出 commit，网速慢时这段时间内，上一页图层的 Preview
    // 会按原坐标叠在新页面上，直到释放后才消失。Resize 等其它连续交互同理。
    //
    // 方案：渲染时按图层所属页面过滤，不改动 Preview 的接管/释放流转与退出事务，切回原页面时若退出尚未
    // 完成，Preview 仍会继续遮住被隐藏的正式 drawable。无限画布同时展示所有画布，不能按页过滤；读不到
    // 所属页面时保持原行为，已删除图层的 Preview 由 flatLayer.onDelete 释放。
    const { edgelessCanvas, frame } = framework.service;
    const visiblePreviews = edgelessCanvas.isEdgelessMode
        ? previews
        : previews.filter(({ layer }) => {
              const frameId = frame.getLayerFrameId(layer.id);
              return frameId === undefined || frameId === activeFrameId;
          });

    if (visiblePreviews.length === 0) return null;

    return (
        <BaseStagePlugin name="vector-layer-svg-preview" zIndex={3}>
            {visiblePreviews.map(({ layer, renderBox, svg }) => (
                <BaseScaleView key={layer.id} layer={layer} style={{ opacity: layer.opacity, overflow: 'visible' }}>
                    <SVGPreviewNode
                        svg={svg}
                        style={{
                            position: 'absolute',
                            left: renderBox.left,
                            top: renderBox.top,
                            width: renderBox.width,
                            height: renderBox.height,
                        }}
                    />
                </BaseScaleView>
            ))}
        </BaseStagePlugin>
    );
});

VectorLayerSVGPreview.displayName = 'VectorLayerSVGPreview';
